import {
  CheckCircleFilled,
  CloseCircleFilled,
  SignatureFilled,
} from '@ant-design/icons';
import { type StatoValidazione } from '@framework/api-client/api/inquiries-compile/schemas/statoValidazione.ts';
import { SubmissionModuleState } from '@framework/common-web/frontoffice/module/fsm/SubmissionModuleState.ts';
import { assertNever } from '@framework/engine/utils/assertNever.ts';
import { t } from '@framework/i18n/intl/i18next.ts';
import { Tooltip } from 'antd';
import clx from 'classnames';
import type * as React from 'react';
import { useMemo } from 'react';
import styled from 'styled-components';
import { SubmissionModuleLogic } from '../../../submission/submission-module/SubmissionModuleLogic.ts';

const ModuleValidationStateIconWrapper = styled.span`
  &.state-error {
    color: ${props => props.theme.moduleStateErrorColor};
  }
  &.state-compilation {
    color: ${props => props.theme.moduleStateValidColor};
  }
  &.state-sign {
    color: ${props => props.theme.moduleStateSignColor};
  }
  &.state-completed {
    color: ${props => props.theme.moduleStateCompletedColor};
  }
`;

/**
 * Ritorna l'icona del tipo di delega
 *
 */
export function getModuleValidationStateTypeIcon(
  type?: SubmissionModuleState,
  isValid?: boolean | null,
): React.ReactNode {
  if (!type) return null;

  if (isValid === false) {
    return <CloseCircleFilled aria-hidden />;
  }

  switch (type) {
    case SubmissionModuleState.Compilation:
      return <CheckCircleFilled aria-hidden />;
    case SubmissionModuleState.Completed:
      return <CheckCircleFilled aria-hidden />;
    case SubmissionModuleState.Sign:
      return <SignatureFilled aria-hidden />;
    case SubmissionModuleState.None:
      return null;
  }
  assertNever(type);
}

function getTooltipText(
  state: SubmissionModuleState,
  isValid?: boolean | null,
) {
  if (isValid === false) {
    return t('module-tooltip-text.modulo-non-valido', 'Modulo non valido');
  }
  switch (state) {
    case SubmissionModuleState.Compilation:
      return t(
        'module-tooltip-text.in-fase-di-compilazione',
        'In fase di compilazione',
      );
    case SubmissionModuleState.Completed:
      return t('module-tooltip-text.completato', 'Completato');
    case SubmissionModuleState.Sign:
      return t(
        'module-tooltip-text.in-attesa-della-firma',
        'In attesa della firma',
      );
    case SubmissionModuleState.None:
      return '';
    default:
      assertNever(state);
  }
}

export interface IModuleValidationStateIconBadgeProps {
  type?: SubmissionModuleState;
  validationState?: StatoValidazione | null;
  /**
   * Se true, nasconde l'icona se il modulo non è mai stato visitato
   * (non è mai stato salvat, `statoValidazione=null`)
   */
  hideIfNoOpen?: boolean;
}

/**
 * Tag di un utente delegato
 */
export function ModuleValidationStateIconBadge(
  props: IModuleValidationStateIconBadgeProps,
) {
  const { type, validationState, hideIfNoOpen } = props;

  const isValidState = SubmissionModuleLogic.isValid(validationState);

  const style = useMemo(() => {
    if (isValidState === false) {
      return 'error';
    }

    return type;
  }, [type, isValidState]);

  if (!type || type === SubmissionModuleState.None) return null;

  if (hideIfNoOpen && !SubmissionModuleLogic.isVisited(validationState))
    return null;

  const tooltipText = getTooltipText(type, isValidState);

  return (
    <Tooltip title={tooltipText}>
      <ModuleValidationStateIconWrapper className={clx(`state-${style}`)}>
        <span className="sr-only"> . {tooltipText}</span>
        {getModuleValidationStateTypeIcon(type, isValidState)}
      </ModuleValidationStateIconWrapper>
    </Tooltip>
  );
}
